type AnyOf = { any_of: number[] }
type AllOf = { all_of: number[] }
type Answered = { answered: [] }
type Equal = { equal: number[] }
type GreaterThan = { greater_than: number[] }
type GreaterThanOrEqual = { greater_than_or_equal: number[] }
type LessThan = { less_than: number[] }
type LessThanOrEqual = { less_than_or_equal: number[] }
type NotAnswered = { not_answered: [] }
type AnswerCondition =
  | AnyOf
  | AllOf
  | Answered
  | Equal
  | GreaterThan
  | GreaterThanOrEqual
  | LessThan
  | LessThanOrEqual
  | NotAnswered

interface QuestionRule {
  id: number
  answer_condition: AnswerCondition
  question_id: number
}
interface RuleGroup {
  id: number
  question_rules: QuestionRule[]
  gate: 'AND' | 'OR'
}
interface Question {
  id: number
  question_rule_groups?: RuleGroup[]
  rows?: Question[]
  type: string
}
interface SurveyResponse {
  id: number
  text_answer: string
  value_answer: number
  answer_id: number
  question_id: number
  recipient_id: number
  sentiment: string
}
type OffsetDirection = 'up' | 'down'

// type for reportErrorFunction
type ReportErrorFunction = (e: Error, action: string, namespace: string) => void

class SurveyQuestionLogician {
  questions: Question[] // array of questions
  surveyResponses: SurveyResponse[] // array of survey responses
  reportErrorFunction: ReportErrorFunction
  indexOfFirstQuestionSection: number // index of the first question section
  questionsMap: Map<number, Question> // Map of questions to improve question search later

  constructor(
    questions: Question[],
    surveyResponses: SurveyResponse[],
    reportErrorFunction: ReportErrorFunction,
    indexOfFirstQuestionSection: number,
  ) {
    this.questions = questions
    this.surveyResponses = surveyResponses
    this.reportErrorFunction = reportErrorFunction || (() => undefined)
    this.indexOfFirstQuestionSection = indexOfFirstQuestionSection || 0
    this.questionsMap = new Map(questions.map((q) => [q.id, q]))
  }

  /**
   * @param {number} destinationIndex
   * @param {'up'|'down'} direction
   * @returns {number}
   * @description
   * This function should return the offset to the next slide.
   * The offset is the number of slides to skip.
   * If the offset is 0, the next slide is the destinationIndex.
   * If the offset is 1, the next slide is the destinationIndex + 1.
   * If the offset is -1, the next slide is the destinationIndex - 1.
   **/
  nextSlideOffset(destinationIndex: number, direction: OffsetDirection): number {
    let rulesPass = this.evaluateCurrentQuestion(destinationIndex)
    if (rulesPass) return 0

    const coefficient = this.convertDirectionToCoefficient(direction)
    let offset = 0

    while (!rulesPass) {
      offset += 1 * coefficient
      rulesPass = this.evaluateCurrentQuestion(destinationIndex + offset)
    }

    return offset
  }

  // Evaluate the current question's rule groups.
  // If a question does not have any rule groups, it passes.
  // If it reaches an index where the question does not exist, it passes because it's the end of the survey.
  evaluateCurrentQuestion(destinationIndex: number) {
    const ruleGroups = this.getRuleGroupsFromIndex(destinationIndex)
    if (!ruleGroups.length) return true

    // For now we only support one rule group
    const ruleGroup = ruleGroups[0]
    return this.evaluateRuleGroup(ruleGroup)
  }

  private evaluateRuleGroup(ruleGroup: RuleGroup): boolean {
    if (ruleGroup.gate === 'AND') {
      return this.evaluateRulesAnd(ruleGroup.question_rules)
    }
    if (ruleGroup.gate === 'OR') {
      return this.evaluateRulesOr(ruleGroup.question_rules)
    }

    const error = Error('Invalid gate')
    this.reportErrorFunction(error, 'evaluateRuleGroup', 'SurveyQuestionLogician')
    throw error
  }

  private convertDirectionToCoefficient(direction: OffsetDirection): number {
    if (direction !== 'down' && direction !== 'up') {
      throw new Error('Invalid direction')
    }
    return direction === 'down' ? 1 : -1
  }

  private getSurveyResponses(questionId: number): SurveyResponse[] {
    const question = this.questionsMap.get(questionId)
    if (!question) return []

    if (question.type !== 'Matrixx') {
      return this.surveyResponses.filter((response) => {
        return response.question_id === questionId
      })
    }

    const questionIds = question.rows.map((row) => row.id)

    return this.surveyResponses.filter((response) => {
      return questionIds.includes(response.question_id)
    })
  }

  private evaluateRule(rule: QuestionRule): boolean {
    // Get the response(s) to the question.
    // This needs to find multiple responses because multiple choice many questions can have multiple response records
    const responses = this.getSurveyResponses(rule.question_id)
    const ruleEvaluator = this.selectRuleEvaluator(rule)

    if (!ruleEvaluator) {
      const error = new Error('Unrecognized condition evaluator')
      this.reportErrorFunction(error, 'evaluateRule', 'SurveyQuestionLogician')
      throw error
    }
    return ruleEvaluator(rule, responses)
  }

  private selectRuleEvaluator(rule: QuestionRule) {
    const evaluators = {
      any_of: this.evaluateAnyOf.bind(this),
      all_of: this.evaluateAllOf.bind(this),
      answered: this.evaluateAnswered.bind(this),
      equal: this.evaluateEqual.bind(this),
      greater_than: this.evaluateGreaterThan.bind(this),
      greater_than_or_equal: this.evaluateGreaterThanOrEqual.bind(this),
      less_than: this.evaluateLowerThan.bind(this),
      less_than_or_equal: this.evaluateLowerThanOrEqual.bind(this),
      not_answered: this.evaluateNotAnswered.bind(this),
    }
    const key = this.extractConditionKeyFromRule(rule)

    return evaluators[key]
  }

  private evaluateAnyOf(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    const answerCondition = rule.answer_condition as AnyOf
    // loop over all response and check if any of their answers are in the array of answer ids
    return responses.some((response) => {
      return answerCondition.any_of.indexOf(response.answer_id) !== -1
    })
  }

  private evaluateAllOf(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    // loop over all expected answers and check if ALL of their answers are in the array of answer ids
    const answerCondition = rule.answer_condition as AllOf
    const answerIds = responses.map((response) => response.answer_id)

    return answerCondition.all_of.every((expectedAnswerId) => {
      return answerIds.indexOf(expectedAnswerId) !== -1
    })
  }

  private evaluateAnswered(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    const question = this.questionsMap.get(rule.question_id)
    if (!question) return false

    if ('Testimonial' === question.type) {
      const answer = responses[0]?.text_answer?.trim()
      return answer?.length > 0
    }

    return responses.length > 0
  }

  private evaluateEqual(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    const answerCondition = rule.answer_condition as Equal
    const answer = responses[0]?.value_answer
    return answerCondition.equal.indexOf(answer) !== -1
  }

  private evaluateGreaterThan(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    const answerCondition = rule.answer_condition as GreaterThan
    const answer = responses[0]?.value_answer
    const target = answerCondition.greater_than[0]
    return answer > target
  }

  private evaluateGreaterThanOrEqual(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    const answerCondition = rule.answer_condition as GreaterThanOrEqual
    const answer = responses[0]?.value_answer
    const target = answerCondition.greater_than_or_equal[0]
    return answer >= target
  }

  private evaluateLowerThan(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    const answerCondition = rule.answer_condition as LessThan
    const answer = responses[0]?.value_answer
    const target = answerCondition.less_than[0]
    return answer < target
  }

  private evaluateLowerThanOrEqual(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    const answerCondition = rule.answer_condition as LessThanOrEqual
    const answer = responses[0]?.value_answer
    const target = answerCondition.less_than_or_equal[0]
    return answer <= target
  }

  private evaluateNotAnswered(rule: QuestionRule, responses: SurveyResponse[]): boolean {
    return !this.evaluateAnswered(rule, responses)
  }

  // This feels 🤮. The currenct shape of the answer_condition is:
  // {any_of: [1, 2, 3]}
  // This messiness would go away if we change the shape to something like:
  // {condition: 'any_of', answer_ids: [1, 2, 3]}
  private extractConditionKeyFromRule(rule: QuestionRule): string {
    return Object.keys(rule.answer_condition)[0]
  }

  private evaluateRulesAnd(rules: QuestionRule[]): boolean {
    return rules.every((rule) => {
      return this.evaluateRule(rule)
    })
  }

  private evaluateRulesOr(rules: QuestionRule[]): boolean {
    return rules.some((rule) => {
      return this.evaluateRule(rule)
    })
  }

  // Return a rule group that corresponds to the intended destination index.
  // If the destination question has no rule group(s), defensively return an empty array
  // It is also possible that the destination is not a question at all. Also return an empty array
  private getRuleGroupsFromIndex(index: number): RuleGroup[] {
    return this.getQuestionFromIndex(index)?.question_rule_groups ?? []
  }

  private getQuestionFromIndex(index: number): Question | undefined {
    // Here we translate the section index to the question index
    const correctedIndex = index - this.indexOfFirstQuestionSection
    return this.questions[correctedIndex]
  }
}

export default SurveyQuestionLogician
